import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { BannerState, ChannelInfo } from "../../../utils/@types";
import { apiService } from "../../../api/api";
import { bannerDashboardApi } from "../../../api/bannerDashboardApi";
import { Toast } from "primereact/toast";
import {
  BannerTemplateBannerState,
  BannerTemplateCurrent,
  BannerTemplateDefault,
} from "./BannerTemplate";
import {
  channelsMissingDefaultBanner,
  isNonDefaultBannerState,
  resolveBannerState,
} from "./bannerStateUtils";
import BannerSwapForm from "./BannerSwapForm/BannerSwapForm";
import MissingDefaultBannerDialog from "./MissingDefaultBannerDialog";
import { ROUTES } from "../../../config/routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseBooleanParam, parseStringArrayParam } from "../../../utils/urlFilters";
import { buildSwapQueuePrefillUrl } from "./bannerSwapQueueNavigation";
import { trackToolAction } from "../../../utils/analytics";
import {
  buildCmsFilterOptions,
  cmsDisplayName,
  mergeCmsDisplayNames,
} from "./cmsDisplayUtils";
import { MultiSelect } from "primereact/multiselect";
import MultiSearch from "../../../Components/SearchInputs/MultiSearch/MultiSearch";
import { InputSwitch } from "primereact/inputswitch";
import { useSelector } from "react-redux";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";

type BannerColumnId =
  | "ChannelTitle"
  | "ChannelId"
  | "CurrentBanner"
  | "DefaultBanner"
  | "Tools"
  | "CMS"
  | "Brand"
  | "Language"
  | "Type"
  | "ChannelManager"
  | "LastChanged"
  | "BannerState"
  | "DefaultBannerStoragePath";

const BANNER_STATE_FILTER_OPTIONS: { label: string; value: BannerState }[] = [
  { label: "Default", value: "default" },
  { label: "No default", value: "no_default" },
  { label: "Brand-boost", value: "brand_boost" },
  { label: "External change", value: "external_change" },
];

const DEFAULT_VISIBLE_COLUMNS: BannerColumnId[] = [
  "ChannelTitle",
  "ChannelId",
  "CurrentBanner",
  "DefaultBanner",
  "Tools",
];

const COLUMN_OPTIONS: { id: BannerColumnId; label: string }[] =
  [
    { id: "ChannelTitle", label: "Channel Name" },
    { id: "ChannelId", label: "Channel Id" },
    { id: "CurrentBanner", label: "Current Banner" },
    { id: "DefaultBanner", label: "Default Banner" },
    { id: "BannerState", label: "Banner state" },
    { id: "Tools", label: "Tools" },
    { id: "CMS", label: "CMS" },
    { id: "Brand", label: "Brands" },
    { id: "Language", label: "Language" },
    { id: "Type", label: "Channel Type" },
    { id: "ChannelManager", label: "Channel Managers" },
    { id: "LastChanged", label: "Last Changed" },
    {
      id: "DefaultBannerStoragePath",
      label: "Default storage path",
    },
  ];

interface ExtendedChannelInfo extends ChannelInfo {
  CMS?: string;
  Language?: string;
  ChannelManager?: string;
  ChannelManagers?: string[]; // Array of channel managers
  ChannelManagerEmail?: string | string[]; // Channel manager email(s) - can be comma-separated string or array
  LastChanged?: string | Date;
  CurrentBannerCachedURL?: string;
  CurrentBannerStoragePath?: string;
  CurrentBannerCachedAt?: string;
  bannerState?: BannerState;
  externalBannerChangeAt?: string | null;
  DefaultBannerURL?: string;
  DefaultBannerStoragePath?: string;
  // Brand?: string | string[]; // Can be single string or array (overrides ChannelInfo.Brand)
}

// --- FilterBar Component ---
const FilterBar = ({
  filters,
  setFilters,
  unfilteredData,
  onSearch,
  onReset,
  myChannelsOnly,
  onMyChannelsToggle,
  visibleColumns,
  setVisibleColumns,
  columnOptions,
  filteredCount,
  onSwapAll,
  showGroupSwap,
  cmsNameById,
}: any) => {
  const cmsOptions = buildCmsFilterOptions(
    unfilteredData.map((item: ExtendedChannelInfo) => item.CMS || ""),
    cmsNameById
  );

  // Flatten brands from arrays and single values
  const brandOptions = Array.from(
    new Set(
      unfilteredData.flatMap((item: ExtendedChannelInfo) => {
        if (!item.Brand) return [];
        if (Array.isArray(item.Brand)) return item.Brand;
        return [item.Brand];
      })
    )
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const languageOptions = Array.from(
    new Set(unfilteredData.map((item: ExtendedChannelInfo) => item.Language))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const channelTypeOptions = Array.from(
    new Set(unfilteredData.map((item: ExtendedChannelInfo) => item.Type))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  // Flatten channel managers from arrays and single values
  // Extract individual manager names (not arrays as single values)
  const channelManagerOptions = Array.from(
    new Set(
      unfilteredData.flatMap((item: ExtendedChannelInfo) => {
        const managers: string[] = [];
        // Add ChannelManagers array if it exists (already an array of individual names)
        if (item.ChannelManagers && Array.isArray(item.ChannelManagers)) {
          managers.push(...item.ChannelManagers);
        }
        // Add single ChannelManager if it exists (could be string or array)
        if (item.ChannelManager) {
          if (Array.isArray(item.ChannelManager)) {
            managers.push(...item.ChannelManager);
          } else {
            managers.push(item.ChannelManager);
          }
        }
        return managers;
      })
    )
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  return (
    <>
      <div className="grid-container">
        <div className="filter-item">
          <span
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            CMS
          </span>
          <MultiSelect
            value={filters.CMS ? [filters.CMS] : []}
            options={cmsOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                CMS: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            optionValue="value"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.ChannelTitle ? [filters.ChannelTitle] : []}
            value={(e) => {
              if (typeof e === "function") {
                const newArray = e([]);
                setFilters((prev: any) => ({
                  ...prev,
                  ChannelTitle: newArray[0] || "",
                }));
              } else {
                setFilters((prev: any) => ({
                  ...prev,
                  ChannelTitle: e[0] || "",
                }));
              }
            }}
            label={"Channel Name"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.ChannelId ? [filters.ChannelId] : []}
            value={(e) => {
              if (typeof e === "function") {
                const newArray = e([]);
                setFilters((prev: any) => ({
                  ...prev,
                  ChannelId: newArray[0] || "",
                }));
              } else {
                setFilters((prev: any) => ({
                  ...prev,
                  ChannelId: e[0] || "",
                }));
              }
            }}
            label={"Channel Id"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <span
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Brands
          </span>
          <MultiSelect
            value={filters.Brand ? [filters.Brand] : []}
            options={brandOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                Brand: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <span
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Language
          </span>
          <MultiSelect
            value={filters.Language ? [filters.Language] : []}
            options={languageOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                Language: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <span
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Channel Type
          </span>
          <MultiSelect
            value={filters.Type ? [filters.Type] : []}
            options={channelTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                Type: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <span style={{ color: "grey", fontSize: "small" }}>
            Channel Managers
          </span>
          <MultiSelect
            value={filters.ChannelManager ? [filters.ChannelManager] : []}
            options={channelManagerOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                ChannelManager: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <span
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Banner state
          </span>
          <MultiSelect
            value={filters.bannerStates ?? []}
            options={BANNER_STATE_FILTER_OPTIONS}
            onChange={(e) => {
              setFilters((prev: any) => ({
                ...prev,
                bannerStates: e.value ?? [],
              }));
            }}
            maxSelectedLabels={2}
            optionLabel="label"
            optionValue="value"
            placeholder="All states"
            style={{ width: "90%", borderRadius: "5px" }}
            showClear
          />
        </div>
        <div className="filter-item">
          <span
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Columns
          </span>
          <MultiSelect
            value={visibleColumns}
            onChange={(e) => setVisibleColumns(e.value ?? [])}
            options={columnOptions}
            optionLabel="label"
            optionValue="id"
            placeholder="Show / hide columns"
            display="chip"
            style={{ width: "90%", borderRadius: "5px" }}
            maxSelectedLabels={2}
            filter
          />
        </div>
      </div>
      <div className="flex justify-between items-center mt-3 pt-1">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label
              htmlFor="myChannelsOnly"
              className="text-sm text-gray-500"
            >
              My Channels Only
            </label>
            <InputSwitch
              id="myChannelsOnly"
              checked={myChannelsOnly}
              onChange={(e) => onMyChannelsToggle(e.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            label="Search"
            icon="pi pi-search"
            onClick={onSearch}
            severity="success"
            className="w-auto"
          />
          <Button
            label="Reset"
            icon="pi pi-replay"
            severity="danger"
            onClick={onReset}
            className="w-auto"
          />
          {showGroupSwap ? (
            <Button
              label={`Create Group Swap (${filteredCount})`}
              icon="pi pi-refresh"
              onClick={onSwapAll}
              disabled={filteredCount === 0}
              severity="info"
              className="w-auto ml-2"
              title="Open a banner swap with all channels matching the current brand, channel type, or language filter"
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

const ChannelBannerViewer: React.FC = () => {
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [acceptingChannelId, setAcceptingChannelId] = useState<string | null>(
    null
  );
  const [missingDefaultDialogOpen, setMissingDefaultDialogOpen] =
    useState(false);
  const [missingDefaultChannels, setMissingDefaultChannels] = useState<
    ExtendedChannelInfo[]
  >([]);

  const defaultBannersPagePath = ROUTES.BANNER_DASHBOARD_DEFAULT_BANNERS;
  const swapQueuePath = ROUTES.BANNER_DASHBOARD_SWAP_QUEUE;

  const [unfilteredData, setUnfilteredData] = useState<ExtendedChannelInfo[]>(
    []
  );
  const [filteredRows, setFilteredRows] = useState<ExtendedChannelInfo[]>([]);
  const [searchParams] = useSearchParams();
  // ?myChannels=true&bannerStates=no_default (e.g. from the home page)
  const [myChannelsOnly, setMyChannelsOnly] = useState<boolean>(
    () => parseBooleanParam(searchParams, "myChannels") === true
  );
  const [cmsNameById, setCmsNameById] = useState<Record<string, string>>(() =>
    mergeCmsDisplayNames([])
  );
  const [visibleColumns, setVisibleColumns] = useState<BannerColumnId[]>(
    DEFAULT_VISIBLE_COLUMNS
  );
  const [filters, setFilters] = useState<any>(() => ({
    CMS: "",
    ChannelTitle: "",
    ChannelId: "",
    Brand: "",
    Language: "",
    Type: "",
    ChannelManager: "",
    bannerStates: parseStringArrayParam(searchParams, "bannerStates") as BannerState[],
  }));

  const columnOptions = COLUMN_OPTIONS;

  // Get current user email from Redux
  const user = useSelector((state: any) => state.googleAuth.user);
  const loggedInUserEmail = user?.email;

  const fetchChannelDetails = async () => {
    try {
      const channelSettingsData =
        await apiService.channelSettings.getAllChannelSettings();
      console.log("Channel Settings Data:", channelSettingsData);

      // Ensure we have an array
      if (!Array.isArray(channelSettingsData)) {
        console.error("Expected array but got:", channelSettingsData);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Invalid data format received from server",
          life: 3000,
        });
        return;
      }

      // Map ChannelSettings to ExtendedChannelInfo format
      const extendedDetails: ExtendedChannelInfo[] = channelSettingsData.map(
        (setting: any) => {
          // Log the DefaultBannerURL for debugging
          console.log(`Mapping channel ${setting.ChannelId || setting.id}:`, {
            ChannelId: setting.ChannelId || setting.id,
            DefaultBannerURL: setting.DefaultBannerURL,
            BannerImageURL: setting.BannerImageURL,
            fullSetting: setting,
          });

          return {
            ChannelId: setting.ChannelId || setting.id || "",
            ChannelTitle: setting.ChannelTitle || "",
            YoutubeContentOwnerId: setting.YoutubeContentOwnerId || "",
            Type: setting.Type || "",
            // Handle Brand as array or string, fallback to OwnerShortName
            Brand: setting.Brand
              ? Array.isArray(setting.Brand)
                ? setting.Brand
                : setting.Brand
              : setting.OwnerShortName || "",
            BannerImageURL: setting.BannerImageURL || "",
            CurrentBannerCachedURL: setting.CurrentBannerCachedURL || "",
            CurrentBannerStoragePath: setting.CurrentBannerStoragePath || "",
            CurrentBannerCachedAt: setting.CurrentBannerCachedAt || "",
            bannerState: setting.bannerState,
            externalBannerChangeAt: setting.externalBannerChangeAt ?? null,
            // Extended fields from ChannelSettings
            CMS: setting.YoutubeContentOwnerId || "", // Map YoutubeContentOwnerId to CMS
            Language: setting.Language || "",
            ChannelManager:
              setting.ChannelManager || setting.channelManager || "",
            ChannelManagers: setting.ChannelManagers || undefined, // Array of channel managers
            ChannelManagerEmail: setting.ChannelManagerEmail || "", // Channel manager email(s)
            LastChanged: setting.LastChanged || "",
            // Include DefaultBannerURL if needed for the component
            DefaultBannerURL: setting.DefaultBannerURL || "",
            DefaultBannerStoragePath:
              setting.DefaultBannerStoragePath || "",
          };
        }
      );
      setUnfilteredData(extendedDetails);
      setFilteredRows(extendedDetails);
    } catch (error) {
      console.error("Error fetching channel settings:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch channel settings",
        life: 3000,
      });
    }
  };

  useEffect(() => {
    fetchChannelDetails();
  }, []);

  useEffect(() => {
    const loadCmsNames = async () => {
      try {
        const owners = await apiService.youtubeCustomIdLookup.listContentOwners();
        setCmsNameById(mergeCmsDisplayNames(owners));
      } catch (error) {
        console.warn("Could not load CMS display names; using fallback map", error);
      }
    };
    void loadCmsNames();
  }, []);

  const handleSearch = useCallback(() => {
    trackToolAction("BannerSwap", "search");
    let filtered = unfilteredData;

    // Apply "My Channels Only" filter first if enabled
    if (myChannelsOnly && loggedInUserEmail) {
      filtered = filtered.filter((r) => {
        if (!r.ChannelManagerEmail) return false;

        // Handle ChannelManagerEmail as string, array, or comma-separated string
        let userEmails: string[] = [];

        if (Array.isArray(r.ChannelManagerEmail)) {
          // If it's already an array, use it directly
          userEmails = r.ChannelManagerEmail.map((email) =>
            String(email).trim().toLowerCase()
          ).filter((email) => email);
        } else if (typeof r.ChannelManagerEmail === "string") {
          // If it's a string, split by comma
          userEmails = r.ChannelManagerEmail.split(",")
            .map((email) => email.trim().toLowerCase())
            .filter((email) => email);
        } else {
          // Fallback: convert to string and try to split
          userEmails = String(r.ChannelManagerEmail)
            .split(",")
            .map((email) => email.trim().toLowerCase())
            .filter((email) => email);
        }

        return userEmails.includes(loggedInUserEmail.toLowerCase());
      });
    }

    if (filters.CMS)
      filtered = filtered.filter((r) =>
        r.CMS?.toLowerCase().includes(filters.CMS.toLowerCase())
      );
    if (filters.ChannelTitle)
      filtered = filtered.filter((r) =>
        r.ChannelTitle?.toLowerCase().includes(
          filters.ChannelTitle.toLowerCase()
        )
      );
    if (filters.ChannelId)
      filtered = filtered.filter((r) =>
        r.ChannelId?.toLowerCase().includes(filters.ChannelId.toLowerCase())
      );
    if (filters.Brand)
      filtered = filtered.filter((r) => {
        if (Array.isArray(r.Brand)) {
          return r.Brand.includes(filters.Brand);
        }
        return r.Brand === filters.Brand;
      });
    if (filters.Language)
      filtered = filtered.filter((r) => r.Language === filters.Language);
    if (filters.Type)
      filtered = filtered.filter((r) => r.Type === filters.Type);
    if (filters.ChannelManager)
      filtered = filtered.filter((r) => {
        const selectedManager = filters.ChannelManager;
        // Check in ChannelManagers array (array of individual names)
        if (r.ChannelManagers && Array.isArray(r.ChannelManagers)) {
          if (r.ChannelManagers.includes(selectedManager)) return true;
        }
        // Check single ChannelManager field (could be string or array)
        if (r.ChannelManager) {
          if (Array.isArray(r.ChannelManager)) {
            if (r.ChannelManager.includes(selectedManager)) return true;
          } else {
            if (r.ChannelManager === selectedManager) return true;
          }
        }
        return false;
      });
    if (filters.bannerStates?.length) {
      const selected = new Set(filters.bannerStates as BannerState[]);
      filtered = filtered.filter((r) =>
        selected.has(resolveBannerState(r))
      );
    }
    setFilteredRows(filtered);
  }, [filters, unfilteredData, myChannelsOnly, loggedInUserEmail]);

  const hasNonDefaultBannerStatesInBatch = useMemo(
    () =>
      filteredRows.some((r) =>
        isNonDefaultBannerState(resolveBannerState(r))
      ),
    [filteredRows]
  );

  const shouldAutoShowBannerStateColumn =
    hasNonDefaultBannerStatesInBatch ||
    (filters.bannerStates?.length ?? 0) > 0;

  const isColumnVisible = useCallback(
    (columnId: BannerColumnId) => {
      if (columnId === "BannerState" && shouldAutoShowBannerStateColumn) {
        return true;
      }
      return visibleColumns.includes(columnId);
    },
    [visibleColumns, shouldAutoShowBannerStateColumn]
  );

  useEffect(() => {
    if (!shouldAutoShowBannerStateColumn) return;
    setVisibleColumns((prev) =>
      prev.includes("BannerState") ? prev : [...prev, "BannerState"]
    );
  }, [shouldAutoShowBannerStateColumn]);

  const handleReset = () => {
    setFilters({
      CMS: "",
      ChannelTitle: "",
      ChannelId: "",
      Brand: "",
      Language: "",
      Type: "",
      ChannelManager: "",
      bannerStates: [],
    });
    setMyChannelsOnly(false);
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setFilteredRows(unfilteredData);
  };

  const handleMyChannelsToggle = (value: boolean) => {
    setMyChannelsOnly(value);
  };

  // Apply filter when myChannelsOnly, loggedInUserEmail, filters, or unfilteredData changes
  useEffect(() => {
    handleSearch();
  }, [myChannelsOnly, loggedInUserEmail, filters, unfilteredData, handleSearch]);

  const openSwapOrRequireDefault = useCallback(
    (channelIds: string[]) => {
      const missing = channelsMissingDefaultBanner(channelIds, unfilteredData);
      if (missing.length > 0) {
        setMissingDefaultChannels(missing);
        setMissingDefaultDialogOpen(true);
        return;
      }
      setSelectedChannelIds(channelIds);
      setShowSwapDialog(true);
    },
    [unfilteredData]
  );

  const handleSwapClick = (channelId: string) => {
    openSwapOrRequireDefault([channelId]);
  };

  const handleSetNewDefaultClick = (channelId: string) => {
    navigate(
      buildSwapQueuePrefillUrl(swapQueuePath, {
        channelId,
        makeDefault: true,
      })
    );
  };

  const handleSwapAllClick = () => {
    const channelIds = Array.from(
      new Set(
        filteredRows.map((row) => row.ChannelId?.trim()).filter(Boolean)
      )
    );
    if (channelIds.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No channels",
        detail: "Apply filters to select channels before using Swap All.",
        life: 3000,
      });
      return;
    }
    openSwapOrRequireDefault(channelIds);
  };

  const handleSetShowSwapDialog: React.Dispatch<React.SetStateAction<boolean>> = (
    value
  ) => {
    setShowSwapDialog((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (!next) {
        setSelectedChannelIds([]);
      }
      return next;
    });
  };

  const patchChannelRow = useCallback(
    (channelId: string, patch: Partial<ExtendedChannelInfo>) => {
      const apply = (rows: ExtendedChannelInfo[]) =>
        rows.map((row) =>
          row.ChannelId === channelId ? { ...row, ...patch } : row
        );
      setUnfilteredData((prev) => apply(prev));
      setFilteredRows((prev) => apply(prev));
    },
    []
  );

  const handleAcceptCurrentAsDefault = useCallback(
    async (channelId: string) => {
      if (acceptingChannelId) {
        return;
      }
      setAcceptingChannelId(channelId);
      try {
        const response =
          await bannerDashboardApi.acceptCurrentBannerAsDefault(channelId);
        const result = response.data ?? response;
        patchChannelRow(channelId, {
          DefaultBannerURL: result.defaultBannerURL,
          DefaultBannerStoragePath: result.defaultBannerStoragePath,
          externalBannerChangeAt: null,
          bannerState: (result.bannerState as BannerState) ?? "default",
        });
        toast.current?.show({
          severity: "success",
          summary: "Default updated",
          detail: `Current banner saved as default for ${channelId}`,
          life: 4000,
        });
      } catch (error: any) {
        console.error("Error accepting current banner as default:", error);
        toast.current?.show({
          severity: "error",
          summary: "Could not update default",
          detail:
            error.response?.data?.message ||
            error.message ||
            "An error occurred",
          life: 5000,
        });
      } finally {
        setAcceptingChannelId(null);
      }
    },
    [acceptingChannelId, patchChannelRow]
  );

  const swapDialogHeader =
    selectedChannelIds.length === 1
      ? `Banner Swap - Channel: ${selectedChannelIds[0]}`
      : `Banner Swap - ${selectedChannelIds.length} channels`;

  const showGroupSwap = Boolean(
    filters.Brand?.trim() ||
      filters.Type?.trim() ||
      filters.Language?.trim()
  );

  const channelLinkBody = (rowData: ExtendedChannelInfo) => {
    const channelUrl = `https://www.youtube.com/channel/${rowData.ChannelId}`;
    return (
      <a
        href={channelUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1A0BD9", textDecoration: "none" }}
      >
        <i className="pi pi-external-link" style={{ marginRight: "4px" }}></i>
        View Channel
      </a>
    );
  };

  const toolsBody = (rowData: ExtendedChannelInfo) => {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <Button
          label="Swap"
          icon="pi pi-refresh"
          size="small"
          onClick={() => handleSwapClick(rowData.ChannelId)}
          className="p-button-sm"
        />
        <Button
          label="Set new default"
          icon="pi pi-image"
          size="small"
          severity="secondary"
          onClick={() => handleSetNewDefaultClick(rowData.ChannelId)}
          className="p-button-sm"
        />
        {channelLinkBody(rowData)}
      </div>
    );
  };

  const cmsBody = (rowData: ExtendedChannelInfo) => {
    return <span>{cmsDisplayName(rowData.CMS, cmsNameById)}</span>;
  };

  const languageBody = (rowData: ExtendedChannelInfo) => {
    return <span>{rowData.Language || "N/A"}</span>;
  };

  const channelManagerBody = (rowData: ExtendedChannelInfo) => {
    // Display ChannelManagers array or single ChannelManager
    // If multiple managers, display each on a separate row
    if (
      rowData.ChannelManagers &&
      Array.isArray(rowData.ChannelManagers) &&
      rowData.ChannelManagers.length > 0
    ) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {rowData.ChannelManagers.map((manager, index) => (
            <span key={index}>{manager}</span>
          ))}
        </div>
      );
    }
    // Handle single ChannelManager (could be string or array)
    if (rowData.ChannelManager) {
      if (
        Array.isArray(rowData.ChannelManager) &&
        rowData.ChannelManager.length > 0
      ) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {rowData.ChannelManager.map((manager, index) => (
              <span key={index}>{manager}</span>
            ))}
          </div>
        );
      }
      return <span>{rowData.ChannelManager}</span>;
    }
    return <span>N/A</span>;
  };

  const defaultBannerBody = (rowData: ExtendedChannelInfo) => (
    <BannerTemplateDefault
      rowData={rowData}
      onAcceptCurrentAsDefault={handleAcceptCurrentAsDefault}
      accepting={acceptingChannelId === rowData.ChannelId}
    />
  );

  const lastChangedBody = (rowData: ExtendedChannelInfo) => {
    if (!rowData.LastChanged) return <span>N/A</span>;

    let date: Date;

    // Handle Firestore Timestamp objects (they have a toDate method)
    if (
      rowData.LastChanged &&
      typeof rowData.LastChanged === "object" &&
      "toDate" in rowData.LastChanged
    ) {
      date = (rowData.LastChanged as any).toDate();
    }
    // Handle string dates
    else if (typeof rowData.LastChanged === "string") {
      date = new Date(rowData.LastChanged);
    }
    // Handle Date objects
    else if (rowData.LastChanged instanceof Date) {
      date = rowData.LastChanged;
    }
    // Fallback: try to create a Date from the value
    else {
      date = new Date(rowData.LastChanged as any);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return <span>Invalid Date</span>;
    }

    return <span>{date.toLocaleDateString()}</span>;
  };

  return (
    <div className="w-full">
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        unfilteredData={unfilteredData}
        onSearch={handleSearch}
        onReset={handleReset}
        myChannelsOnly={myChannelsOnly}
        onMyChannelsToggle={handleMyChannelsToggle}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        columnOptions={columnOptions}
        filteredCount={filteredRows.length}
        onSwapAll={handleSwapAllClick}
        showGroupSwap={showGroupSwap}
        cmsNameById={cmsNameById}
      />
      <DataTable
        key={[
          ...visibleColumns,
          shouldAutoShowBannerStateColumn ? "BannerState" : "",
        ].join("|")}
        className="mt-4"
        value={filteredRows}
        paginator
        paginatorLeft
        paginatorTemplate="firstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        showGridlines
        tableStyle={{ minWidth: "50rem", maxWidth: "100%" }}
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
      >
        {isColumnVisible("CMS") && (
          <Column field="CMS" header="CMS" body={cmsBody} sortable />
        )}
        {isColumnVisible("ChannelTitle") && (
          <Column field="ChannelTitle" header="Channel Name" sortable />
        )}
        {isColumnVisible("ChannelId") && (
          <Column field="ChannelId" header="Channel Id" sortable />
        )}
        {isColumnVisible("Brand") && (
          <Column
            field="Brand"
            header="Brands"
            body={(rowData: ExtendedChannelInfo) => {
              if (Array.isArray(rowData.Brand) && rowData.Brand.length > 0) {
                return <span>{rowData.Brand.join(", ")}</span>;
              }
              return <span>{rowData.Brand || "N/A"}</span>;
            }}
            sortable
          />
        )}
        {isColumnVisible("Language") && (
          <Column
            field="Language"
            header="Language"
            body={languageBody}
            sortable
          />
        )}
        {isColumnVisible("Type") && (
          <Column field="Type" header="Channel Type" sortable />
        )}
        {isColumnVisible("ChannelManager") && (
          <Column
            field="ChannelManager"
            header="Channel Managers"
            body={channelManagerBody}
            sortable
          />
        )}
        {isColumnVisible("CurrentBanner") && (
          <Column
            header="Current Banner"
            body={BannerTemplateCurrent}
            style={{ minWidth: "15rem", width: "15rem" }}
          />
        )}
        {isColumnVisible("DefaultBanner") && (
          <Column
            header="Default Banner"
            body={defaultBannerBody}
            style={{ minWidth: "15rem", width: "15rem" }}
          />
        )}
        {isColumnVisible("BannerState") && (
          <Column
            field="bannerState"
            header="Banner state"
            body={BannerTemplateBannerState}
            sortable
            sortFunction={(event: any) =>
              [...event.data].sort((a, b) => {
                const result = resolveBannerState(a).localeCompare(
                  resolveBannerState(b)
                );
                return (event.order ?? 1) * result;
              })
            }
          />
        )}
        {isColumnVisible("DefaultBannerStoragePath") && (
          <Column
            field="DefaultBannerStoragePath"
            header="Default storage path"
            body={(rowData: ExtendedChannelInfo) => (
              <span
                className="text-xs font-mono text-gray-600 break-all"
                title={rowData.DefaultBannerStoragePath}
              >
                {rowData.DefaultBannerStoragePath?.trim() || "—"}
              </span>
            )}
            style={{ minWidth: "12rem" }}
          />
        )}
        {isColumnVisible("LastChanged") && (
          <Column
            field="LastChanged"
            header="Last Changed"
            body={lastChangedBody}
            sortable
          />
        )}
        {isColumnVisible("Tools") && (
          <Column header="Tools" body={toolsBody} />
        )}
      </DataTable>
      <BannerSwapForm
        showDialog={showSwapDialog}
        setShowDialog={handleSetShowSwapDialog}
        toast={toast}
        initialChannelIds={
          selectedChannelIds.length > 0 ? selectedChannelIds : undefined
        }
        dialogHeader={swapDialogHeader}
        onSwapQueued={fetchChannelDetails}
      />
      <MissingDefaultBannerDialog
        visible={missingDefaultDialogOpen}
        onHide={() => {
          setMissingDefaultDialogOpen(false);
          setMissingDefaultChannels([]);
        }}
        channels={missingDefaultChannels.map((row) => ({
          channelId: row.ChannelId,
          channelTitle: row.ChannelTitle,
        }))}
        defaultBannersPath={defaultBannersPagePath}
      />
      <Toast ref={toast} />
    </div>
  );
};

export default ChannelBannerViewer;
